import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Bookmark, ArrowLeft, Trash2, Highlighter, Folder, Plus, X } from "lucide-react";
import {
    listBookmarks, deleteBookmarksByRefs, updateBookmark, listBookmarkGroups,
    listHighlights, HIGHLIGHT_PALETTE
} from "@/lib/api";

const TABS = [
    { key: "bookmarks", label: "Saved", icon: Bookmark },
    { key: "highlights", label: "Highlights", icon: Highlighter },
];

export default function SavedPage() {
    const [sp, setSp] = useSearchParams();
    const [tab, setTab] = useState(sp.get("tab") || "bookmarks");
    const [group, setGroup] = useState(sp.get("group") || "");
    const [bookmarks, setBookmarks] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingGroups, setEditingGroups] = useState(null); // bookmark_id
    const nav = useNavigate();

    const load = () => {
        setLoading(true);
        Promise.all([
            listBookmarks().then(d => setBookmarks(d.bookmarks || [])),
            listHighlights().then(d => setHighlights(d.highlights || [])),
            listBookmarkGroups().then(d => setGroups(d.groups || [])),
        ]).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const next = new URLSearchParams();
        if (tab !== "bookmarks") next.set("tab", tab);
        if (group) next.set("group", group);
        setSp(next, { replace: true });
    }, [tab, group, setSp]);

    const filteredBookmarks = useMemo(() => {
        if (!group) return bookmarks;
        return bookmarks.filter(b => (b.groups || []).includes(group));
    }, [bookmarks, group]);

    return (
        <div
            data-testid="saved-page"
            className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full"
            style={{ maxWidth: "72ch" }}
        >
            <button
                onClick={() => nav("/read")}
                className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink mb-3"
            >
                <ArrowLeft size={14} /> Back to reader
            </button>

            <div className="flex items-center gap-3 mb-1">
                <Bookmark size={22} className="text-moss-deep" />
                <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Saved</h1>
            </div>
            <p className="font-ui text-[13px] text-ink-70 mb-5">
                Bookmarked passages and colour-coded highlights, all in one place.
            </p>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-ink-10 mb-5">
                {TABS.map(t => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            data-testid={`saved-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={
                                "px-4 py-2.5 -mb-px font-ui text-[13px] font-medium inline-flex items-center gap-2 border-b-2 transition " +
                                (active
                                    ? "border-moss-deep text-moss-deep"
                                    : "border-transparent text-ink-40 hover:text-ink")
                            }
                        >
                            <Icon size={14} /> {t.label}
                            <span className="text-[11px] text-ink-40 font-normal">
                                {t.key === "bookmarks" ? bookmarks.length : highlights.length}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Group filter (bookmarks tab only) */}
            {tab === "bookmarks" && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40">Groups</span>
                    <GroupChip active={!group} onClick={() => setGroup("")}>All</GroupChip>
                    {groups.map(g => (
                        <GroupChip key={g} active={group === g} onClick={() => setGroup(g)}>{g}</GroupChip>
                    ))}
                </div>
            )}

            {loading ? (
                <p className="font-ui text-sm text-ink-40">Loading…</p>
            ) : tab === "bookmarks" ? (
                filteredBookmarks.length === 0 ? (
                    <EmptyState kind="bookmarks" group={group} />
                ) : (
                    <ul className="space-y-2">
                        {filteredBookmarks.map(b => (
                            <BookmarkItem
                                key={b.id}
                                b={b}
                                editing={editingGroups === b.id}
                                allGroups={groups}
                                onEdit={() => setEditingGroups(editingGroups === b.id ? null : b.id)}
                                onSave={async (nextGroups) => {
                                    await updateBookmark(b.id, { groups: nextGroups });
                                    setEditingGroups(null);
                                    load();
                                }}
                                onDelete={async () => { await deleteBookmarksByRefs(b.refs); load(); }}
                            />
                        ))}
                    </ul>
                )
            ) : (
                highlights.length === 0 ? (
                    <EmptyState kind="highlights" />
                ) : (
                    <ul className="space-y-2">
                        {highlights.map(h => (
                            <HighlightItem key={h.id} h={h} />
                        ))}
                    </ul>
                )
            )}
        </div>
    );
}

function GroupChip({ active, onClick, children }) {
    return (
        <button
            data-testid={`group-chip-${String(children).toLowerCase().replace(/\s+/g, "-")}`}
            onClick={onClick}
            className={
                "px-3 py-1 rounded-full font-ui text-[12px] transition inline-flex items-center gap-1 " +
                (active
                    ? "bg-moss text-white"
                    : "bg-white border border-ink-10 text-ink-70 hover:border-moss/40 hover:text-ink")
            }
        >
            {children !== "All" && <Folder size={11} />} {children}
        </button>
    );
}

function EmptyState({ kind, group }) {
    return (
        <div className="mt-10 text-center">
            <p className="scripture text-[18px] text-ink-70">
                {kind === "bookmarks"
                    ? (group ? `Nothing tagged "${group}" yet.` : "Nothing bookmarked yet.")
                    : "No highlights yet."}
            </p>
            <p className="mt-1 font-ui text-[13px] text-ink-40">
                {kind === "bookmarks"
                    ? "Open a verse in the reader and tap the star to save it here."
                    : "Select verses in the reader to highlight them."}
            </p>
        </div>
    );
}

function BookmarkItem({ b, editing, allGroups, onEdit, onSave, onDelete }) {
    const [localGroups, setLocalGroups] = useState(b.groups || []);
    const [newTag, setNewTag] = useState("");

    useEffect(() => { setLocalGroups(b.groups || []); }, [b, editing]);

    const toggleGroup = (g) => {
        setLocalGroups(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
    };
    const addNewTag = () => {
        const t = newTag.trim();
        if (!t) return;
        setLocalGroups(prev => prev.includes(t) ? prev : [...prev, t]);
        setNewTag("");
    };

    return (
        <li className="p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md transition">
            <div className="flex items-start justify-between gap-3">
                <Link
                    data-testid={`bookmark-item-${b.id}`}
                    to={`/?chapter=${b.chapter || 1}${b.verse ? `&verse=${b.verse}` : ""}`}
                    className="flex-1 min-w-0"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <Bookmark size={12} className="text-moss-deep fill-moss-deep" />
                        <span className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                            {b.label}
                        </span>
                        {(b.groups || []).map(g => (
                            <span
                                key={g}
                                data-testid={`bookmark-group-tag-${g}`}
                                className="px-2 py-0.5 rounded-full bg-sage-paper text-ink-70 font-ui text-[10px] inline-flex items-center gap-1"
                            >
                                <Folder size={9} /> {g}
                            </span>
                        ))}
                    </div>
                    {b.preview && (
                        <p className="scripture text-[15px] text-ink mt-1 leading-snug line-clamp-3">{b.preview}</p>
                    )}
                    {b.note && (
                        <p className="mt-2 font-ui text-[12px] italic text-ink-70 line-clamp-2">"{b.note}"</p>
                    )}
                </Link>
                <div className="shrink-0 flex flex-col items-center gap-1">
                    <button
                        data-testid={`bookmark-edit-groups-${b.id}`}
                        onClick={onEdit}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-ink-40 hover:text-moss-deep hover:bg-moss/10 transition"
                        aria-label="Edit groups"
                        title="Edit groups"
                    >
                        <Folder size={14} />
                    </button>
                    <button
                        data-testid={`bookmark-delete-${b.id}`}
                        onClick={onDelete}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-ink-40 hover:text-plum hover:bg-plum/10 transition"
                        aria-label="Remove bookmark"
                        title="Remove bookmark"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
            {editing && (
                <div
                    data-testid={`bookmark-group-editor-${b.id}`}
                    className="mt-3 pt-3 border-t border-ink-10"
                >
                    <div className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 mb-2">
                        Assign groups
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {[...new Set([...allGroups, ...localGroups])].map(g => {
                            const on = localGroups.includes(g);
                            return (
                                <button
                                    key={g}
                                    onClick={() => toggleGroup(g)}
                                    className={
                                        "px-2.5 py-1 rounded-full font-ui text-[11px] transition inline-flex items-center gap-1 " +
                                        (on
                                            ? "bg-moss text-white"
                                            : "bg-sage-paper text-ink-70 hover:bg-ink-10")
                                    }
                                >
                                    <Folder size={10} /> {g}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            data-testid={`bookmark-new-group-input-${b.id}`}
                            value={newTag}
                            onChange={e => setNewTag(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && addNewTag()}
                            placeholder="New group (e.g. Sermon Prep, Comfort, Study)"
                            className="flex-1 px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] focus:outline-none focus:border-moss/60"
                        />
                        <button
                            onClick={addNewTag}
                            className="w-8 h-8 rounded-full bg-ink-10 hover:bg-moss/20 flex items-center justify-center transition"
                            aria-label="Add group"
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            onClick={onEdit}
                            className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70 hover:bg-ink-10"
                        >
                            <X size={12} className="inline mr-1" /> Cancel
                        </button>
                        <button
                            data-testid={`bookmark-save-groups-${b.id}`}
                            onClick={() => onSave(localGroups)}
                            className="px-3 py-1.5 rounded-full font-ui text-[12px] bg-moss text-white hover:bg-moss-deep"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
}

function HighlightItem({ h }) {
    const swatch = HIGHLIGHT_PALETTE.find(p => p.name === h.color)?.value || "#B7CDB1";
    return (
        <li className="p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md transition">
            <Link
                to={`/?chapter=${h.chapter || 1}${h.verse ? `&verse=${h.verse}` : ""}`}
                className="block"
            >
                <div className="flex items-center gap-2">
                    <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: swatch }}
                    />
                    <span className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                        {h.label}
                    </span>
                </div>
                {h.preview && (
                    <p className="scripture text-[15px] text-ink mt-1 leading-snug line-clamp-3">{h.preview}</p>
                )}
            </Link>
        </li>
    );
}
